import { LoadingOverlay, Select } from "@mantine/core";
import { DateRangePicker } from "@mantine/dates";
import { ChevronDownIcon } from "@modulz/radix-icons";
import dayjs from "dayjs";
import moment from "moment";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useTokenDetails } from "../../context/token-details-context";
import { useGetWalletGraphData } from "../../hooks/useWallets";
import { chartSelectStyles } from "../../lib/constants";
import {
	formatAmountWithDecimals,
	getDateMonthFromTimestamp,
	getMonthFromTimestamp,
} from "../../lib/utils";
import type { WalletGraphRequest } from "../../services/wallet-service";
import { Paragraph, ParagraphBold, Title } from "../styled";
import { ReAreaChart } from "./AreaChart";

export const WalletBalanceChart = (): JSX.Element => {
	const { t, i18n } = useTranslation();

	const { tokenDetails, walletSummaryDetails } = useTokenDetails();
	const tokenId = tokenDetails?.[0].id;
	const tokenSymbol = tokenDetails?.[0].symbol;

	const [isDatePickerVisible, setIsDatePickerVisible] =
		useState<boolean>(false);
	const [period, setPeriod] = useState("180");

	const [dateFilters, setDateFilters] = useState<[Date | null, Date | null]>([
		null,
		null,
	]);

	const wallets = walletSummaryDetails?.wallets || [];
	const distributionWallet = wallets?.find(
		(wallet) => wallet?.category === "Distribution",
	);

	// Chart wallet graph request
	const chartRequest: WalletGraphRequest = {
		tokenId,
		data: {
			walletId: distributionWallet?.id,
			numDays: Number(period),
		},
	};

	if (dateFilters[0] && dateFilters[1]) {
		chartRequest.data.startDate = moment(dateFilters[0]).format("YYYY-MM-DD");
		chartRequest.data.endDate = moment(dateFilters[1]).format("YYYY-MM-DD");
		chartRequest.data.numDays = undefined;
	}
	const enableChart =
		(Boolean(dateFilters[0] && dateFilters[1]) || period !== "custom") &&
		Boolean(tokenId) &&
		Boolean(distributionWallet?.id);
	const { isLoading: isLoadingGraph, data } = useGetWalletGraphData(
		{ ...chartRequest },
		enableChart,
	);

	const creditChartData = data?.creditData || {};
	const debitChartData = data?.debitData || {};
	const creditAmount = data?.credit || 0;
	const debitAmount = data?.debit || 0;

	const getXAxisPoints = (time) => {
		const locale = i18n.resolvedLanguage;

		if (dateFilters[0] && dateFilters[1]) {
			return getDateMonthFromTimestamp(time, locale);
		}
		switch (period) {
			case "7":
				return getDateMonthFromTimestamp(time, locale);
			case "14":
				return getDateMonthFromTimestamp(time, locale);
			case "30":
				return getDateMonthFromTimestamp(time, locale);
			default:
				return getMonthFromTimestamp(time, locale);
		}
	};

	const constructGraphData = () => {
		const graphData = [];
		const timeStamps = Object.keys(creditChartData).sort();
		const divisor = 10 ** walletSummaryDetails?.decimals;
		for (const timeStamp of timeStamps) {
			const creditAmount = Number(creditChartData[timeStamp]) / divisor || 0;
			const debitAmount = Number(debitChartData[timeStamp]) / divisor || 0;
			graphData.push({
				name: getXAxisPoints(timeStamp),
				[t("credit")]: Number(creditAmount),
				[t("debit")]: Number(debitAmount),
			});
		}
		return graphData;
	};

	const onWalletDurationChange = (val) => {
		setPeriod(() => val);
		setDateFilters([null, null]);

		if (val === "custom") {
			setIsDatePickerVisible(true);
		} else {
			setIsDatePickerVisible(false);
		}
	};

	const walletDurationOptions = [
		{ label: t("duration.one.week"), value: "7" },
		{ label: t("duration.two.weeks"), value: "14" },
		{ label: t("duration.one.month"), value: "30" },
		{ label: t("duration.three.months"), value: "90" },
		{ label: t("duration.six.months"), value: "180" },
		{ label: t("duration.nine.months"), value: "270" },
		{ label: t("duration.custom"), value: "custom" },
	];

	const decimals = walletSummaryDetails?.decimals;
	return (
		<Wrapper>
			<LoadingOverlay
				visible={isLoadingGraph}
				zIndex={5}
				overlayColor="var(--accentBg)"
			/>
			<TopSection>
				<LeftSection>
					<Title style={{ textTransform: "none" }}>
						{t("wallets.balance")}
					</Title>
					<Amount>
						{formatAmountWithDecimals(
							Number(distributionWallet?.balances?.[0]?.amount),
							decimals,
						) || 0}{" "}
						{walletSummaryDetails?.symbol}
					</Amount>
				</LeftSection>
				<RightSection>
					<WalletTypeLabel>{t("distribution.title")}</WalletTypeLabel>
					<WalletSection style={{ marginBottom: 12 }}>
						<CreditLabel>{t("credit")}</CreditLabel>
						<ParagraphBold>
							{formatAmountWithDecimals(creditAmount, decimals)}{" "}
							{walletSummaryDetails?.symbol}
						</ParagraphBold>
					</WalletSection>
					<WalletSection>
						<DebitLabel>{t("debit")}</DebitLabel>
						<ParagraphBold>
							{formatAmountWithDecimals(debitAmount, decimals)}{" "}
							{walletSummaryDetails?.symbol}
						</ParagraphBold>
					</WalletSection>
				</RightSection>
			</TopSection>

			<DatePickerWrapper>
				<ChartTitle>{t("distribution.wallet.transactions")}</ChartTitle>
				<Select
					label=""
					onChange={onWalletDurationChange}
					value={period}
					rightSection={<ChevronDownIcon />}
					styles={{
						...chartSelectStyles,
						root: {
							...chartSelectStyles.root,
							marginBottom: 0,
						},
					}}
					data={walletDurationOptions}
					maxDropdownHeight={280}
				/>
				  {isDatePickerVisible && (
                      <DateRangePicker
                        placeholder="Pick custom date range"
                        value={dateFilters}
                        onChange={setDateFilters}
                        initiallyOpened
                        maxDate={dayjs(new Date()).toDate()}
                        styles={{
                          root: {
                            backgroundColor: "var(--accentBg)",
                          },
                          dropdown: {
                            backgroundColor: "var(--accentBg)",
                            border: "none",
                            color: "var(--textColor)",
                          },
                          input: {
                            backgroundColor: "var(--accentBg)",
                            color: "var(--textColor)",
                            border: "1px solid var(--borderColor)",
                          },
                          calendarHeader: {
                            backgroundColor: "var(--accentBg)",
                            color: "var(--textColor)",
                          },
                          calendarHeaderControl: {
                            backgroundColor: "var(--accentBg)",
                            color: "var(--textColor)",
                            "&:hover": {
                              backgroundColor: "transparent",
                            },
                          },
                          calendarHeaderLevel: {
                            backgroundColor: "var(--accentBg)",
                            color: "var(--textColor)",
                            "&:hover": {
                              backgroundColor: "transparent",
                            },
                          },
                          arrow: {
                            display: "none"
                          },
                          monthPickerControl: {
                            backgroundColor: "var(--accentBg)",
                            color: "var(--textColor)",
                            "&:hover": {
                              backgroundColor: "transparent",
                              color: "var(--textColor)",
                            },
                          },
                        }}
                      />
                    )}
			</DatePickerWrapper>
			<ReAreaChart data={constructGraphData()} tokenSymbol={tokenSymbol} />
		</Wrapper>
	);
};

const ChartTitle = styled.p`
  font-size: 12px;
  line-height: 18px;
  margin: 0;
  font-family: 'ProximaNova', sans-serif;
  color: var(--textColor);
`;

const Wrapper = styled.div`
  width: 100%;
  background-color: var(--accentBg);
  border-radius: 8px;
  padding: 24px;
  position: relative;
  margin-top: 42px;
`;

const DatePickerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TopSection = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.primary.powderBlue};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 15px;
  margin-bottom: 16px;
`;

const LeftSection = styled.div``;
const RightSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const WalletTypeLabel = styled(ParagraphBold)`
  align-items: center;
  margin-bottom: 16px;
  color: ${({ theme }) => theme.colors.primary.green};
  font-size: 17px;
  font-weight: 600;
`;

const Amount = styled.p`
  font-size: 28px;
  line-height: 42px;
  margin: 0;
  margin-top: 20px;
  font-family: 'ProximaNovaBold', sans-serif;
  color: ${({ theme }) => theme.colors.primary.green};
`;
const WalletSection = styled.div`
  display: flex;
  justify-content: space-between;
`;

const CreditLabel = styled(Paragraph)`
  color: ${({ theme }) => theme.colors.primary.green};
  margin-right: 20px;
`;

const DebitLabel = styled(Paragraph)`
  color: ${({ theme }) => theme.colors.secondary.red};
  margin-right: 20px;
`;
